/**
 * Client service for Ops record photo uploads and URL resolution.
 * R39 A4.b (plan docs/plans/sector-operations.md SO-P59).
 * Path: workspaces/{workspaceId}/ops_records/{recordId}/{uid}/{fileId}
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { firebaseStorage, firebaseAuth } from './firebase'
import { getNlsString } from '../nls/nlsRuntime'

export const ALLOWED_PHOTO_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const

export const MAX_PHOTO_BYTES = 10 * 1024 * 1024 // 10 MB

const EXTENSION_MAP: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
}

function getSafeExtension(file: File): string {
  const parts = file.name.split('.')
  if (parts.length > 1) {
    const ext = parts.pop()!.toLowerCase().replace(/[^a-z0-9]/g, '')
    if (['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(ext)) {
      return ext === 'jpeg' ? 'jpg' : ext
    }
  }
  return EXTENSION_MAP[file.type] || 'jpg'
}

export interface UploadRecordPhotoResult {
  path: string
  downloadUrl: string
}

export async function uploadRecordPhoto(
  workspaceId: string,
  recordId: string,
  file: File,
): Promise<UploadRecordPhotoResult> {
  if (!workspaceId) throw new Error('workspaceId is required.')
  if (!recordId) throw new Error('recordId is required.')

  if (!ALLOWED_PHOTO_MIME_TYPES.includes(file.type as (typeof ALLOWED_PHOTO_MIME_TYPES)[number])) {
    throw new Error(getNlsString('NLSOpsPhotoInvalidType'))
  }

  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error(getNlsString('NLSOpsPhotoTooLarge'))
  }

  const uid = firebaseAuth.currentUser?.uid
  if (!uid) {
    throw new Error('Authentication required for photo upload.')
  }

  const ext = getSafeExtension(file)
  const randomSuffix = Math.random().toString(36).slice(2, 10)
  const fileId = `${randomSuffix}_${Date.now()}.${ext}`
  const path = `workspaces/${workspaceId}/ops_records/${recordId}/${uid}/${fileId}`

  const storageRef = ref(firebaseStorage, path)
  await uploadBytes(storageRef, file, {
    contentType: file.type,
  })

  const downloadUrl = await getDownloadURL(storageRef)
  return { path, downloadUrl }
}

export async function getRecordPhotoUrl(path: string): Promise<string> {
  if (!path) return ''
  const storageRef = ref(firebaseStorage, path)
  return getDownloadURL(storageRef)
}
